import {
  OPTION_DELIMITER,
  OTHER_ANSWER_SUFFIX,
  PROPERTY_NAME,
  PROPERTY_VALUE,
  SURVEY,
  TYPE_KEY
} from "../common/constant";
import { FilesModel as $FILES, RespondentsOverviewModel as roModel } from "../common/entities";
import ObjectUtils, { coalesce } from "../utils/ObjectUtils";
import { cleanText, isSimpleType, makeAnswer, makeSimpleAnswer } from "../utils/QuestionUtils";
import { parseImportedSurveyDate } from "../utils/SurveyDateUtils";
import { beginLoading, endLoading } from "./loadingSpinner";
import { createBackendErrorMessages, createErrorMessages, createSuccessMessages, messagesReceived } from "./message";
import { LOAD_SURVEY_RESULT_ACTION, resetSurveyRunner, RESULT_VALUE, saveSurveyResult } from "./surveyRunner";

import download from "downloadjs";
import * as uuid from "uuid";
import rest from "../common/rest";
import ValidatorUtils, { PREVENT_UPDATE } from "../utils/ValidatorUtils";
import { showDialog } from "./confirmDialog";
import { SURVEY_TEMPLATE } from "./survey";

const {
  findObjectIndexInArray,
  filterExcludeByTemplate,
  opt,
  getDeepProperty,
  findByTemplate,
  findByObjectProp,
  removeObjectByProp,
  indexByProperty
} = ObjectUtils;
const JUMP_TO_PAGE_ACTION = "SURVEY_RUNNER.JUMP_TO_PAGE_ACTION";
const SHOW_HIDE_OVERVIEW_ACTION = "SURVEY_RUNNER.SHOW_HIDE_OVERVIEW_ACTION";
const RESET_ACTION = "SURVEY_VIEW.RESET_ACTION";
const REFRESH_TRIGGERS_ACTION = "SURVEY_RUNNER.REFRESH_TRIGGERS_ACTION";
const SET_ANSWER_ACTION = "SURVEY_RUNNER.SET_ANSWER_ACTION";
const ADD_ANSWER_ACTION = "SURVEY_RUNNER.ADD_ANSWER_ACTION";
const REMOVE_ANSWER_ACTION = "SURVEY_RUNNER.REMOVE_ANSWER_ACTION";
const VALIDATE_ANSWER_ACTION = "SURVEY_RUNNER.VALIDATE_ANSWER_ACTION";
const ADD_TABLE_ROW_ACTION = "SURVEY_RUNNER.ADD_TABLE_ROW_ACTION";
const UPDATE_TABLE_ROW_ACTION = "SURVEY_RUNNER.UPDATE_TABLE_ROW_ACTION";
const DELETE_TABLE_ROW_ACTION = "SURVEY_RUNNER.DELETE_TABLE_ROW_ACTION";
const SELECT_FILES_ACTION = "SURVEY_RUNNER.SELECT_FILES_ACTION";
const SUCCESSFUL_UPLOAD_ACTION = "SURVEY_RUNNER.SUCCESSFUL_UPLOAD_ACTION";
const PROPERTY_CHANGED_ACTION = "SURVEY_RUNNER.PROPERTY_CHANGED_ACTION";
const REORDER_QUESTION_ROW_ACTION = "SURVEY_RUNNER.REORDER_QUESTION_ROW_ACTION";
const IMPORT_TABLE_ROWS_ACTION = "SURVEY_RUNNER.IMPORT_TABLE_ROWS_ACTION";
const LOAD_SURVEY_PREVIOUS_RESULT_ACTION = "SURVEY_RUNNER.LOAD_SURVEY_PREVIOUS_RESULT_ACTION";

const ANSWER = "answer";
const QUESTION_NAME = "questionName";
const QUESTION_MODEL = "questionModel";
const VALIDATION = "validation";
const ID = "id";
const FIRST_ONLY = "firstOnly";
export const SURVEY_CURRENT_PAGE = "surveyCurrentPage";
export const SHOW_OVERVIEW_MENU = "showOverviewMenu";
export const SURVEY_RESULT = "surveyResult";
export const SURVEY_PREV_RESULT = "surveyPrevResult";
export const QUESTIONS = "questions";
export const QUESTION_VALIDATIONS = "questionValidations";
export const TRIGGER_STATE = "triggerState";
export const OTHER_ANSWER_ID = "OtherAnswer";
export const EDITING_DISABLED = "editingDisabled";
export const SURVEY_RESULT_CHANGED = "surveyResultChanged";
const COLUMN_NAME = "columnName";
const ROW_NAME = "rowName";
const ROW_TO_MOVE_ID = "rowToMove";
const ROW_TARGET_ID = "rowTargetId";
const FILES = "files";
const FILE = "file";
const IMPORT_DATA = "importData";
const OVERWRITE = "overwrite";
const CHUNKED_TABLE_IMPORT_ENABLED = false;
const TABLE_IMPORT_CHUNK_SIZE = 250;

const initialState = {
  [SURVEY_CURRENT_PAGE]: "",
  [SHOW_OVERVIEW_MENU]: true,
  [SURVEY_RESULT]: { [QUESTIONS]: [] },
  [QUESTION_VALIDATIONS]: {},
  [TRIGGER_STATE]: [],
  [EDITING_DISABLED]: false,
  [SURVEY_RESULT_CHANGED]: false,
  [IMPORT_DATA]: null
};

let reducer = (state = ObjectUtils.deepClone(initialState), action) => {
  let newState;
  let surveyResult;
  let triggerState;
  switch (action[TYPE_KEY]) {
    case RESET_ACTION:
      newState = Object.assign({}, state, {
        [SURVEY_CURRENT_PAGE]: "",
        [SHOW_OVERVIEW_MENU]: true,
        [SURVEY_RESULT]: { [QUESTIONS]: [] },
        [QUESTION_VALIDATIONS]: {},
        [TRIGGER_STATE]: [],
        [EDITING_DISABLED]: false,
        [SURVEY_RESULT_CHANGED]: false,
        [IMPORT_DATA]: null
      });
      break;
    case JUMP_TO_PAGE_ACTION:
      triggerState = refershTriggers(action[SURVEY_TEMPLATE], state[SURVEY_RESULT]);
      newState = handleJumpToPage(state, action, triggerState);
      break;
    case LOAD_SURVEY_RESULT_ACTION:
      let result = action[RESULT_VALUE];
      newState = Object.assign({}, state, { [SURVEY_RESULT]: prepareResult(result) });
      break;
    case LOAD_SURVEY_PREVIOUS_RESULT_ACTION:
      let prevResult = action[SURVEY_PREV_RESULT];
      newState = Object.assign({}, state, { [SURVEY_PREV_RESULT]: prepareResult(prevResult) });
      break;
    case REFRESH_TRIGGERS_ACTION:
      triggerState = refershTriggers(action[SURVEY_TEMPLATE], state[SURVEY_RESULT]);
      newState = Object.assign({}, state, { [TRIGGER_STATE]: triggerState });
      break;
    case SHOW_HIDE_OVERVIEW_ACTION:
      newState = Object.assign({}, state, { [SHOW_OVERVIEW_MENU]: action[SHOW_OVERVIEW_MENU] });
      break;
    case SET_ANSWER_ACTION:
      surveyResult = handleSetAnswer(
        state[SURVEY_RESULT],
        action[QUESTION_MODEL],
        action[ANSWER],
        action[PREVENT_UPDATE],
        action[SURVEY_TEMPLATE]
      );
      triggerState = handleTriggers(state[TRIGGER_STATE], action[QUESTION_MODEL], surveyResult, true, action[ANSWER]);
      newState = Object.assign({}, state, {
        [SURVEY_RESULT]: surveyResult,
        [TRIGGER_STATE]: triggerState
      });
      break;
    case SELECT_FILES_ACTION:
      surveyResult = !action[PREVENT_UPDATE]
        ? handleSelectFiles(state[SURVEY_RESULT], action[QUESTION_MODEL], action[FILES])
        : state[SURVEY_RESULT];
      newState = Object.assign({}, state, { [SURVEY_RESULT]: surveyResult });
      break;
    case SUCCESSFUL_UPLOAD_ACTION:
      surveyResult = handleSuccessfulUpload(state[SURVEY_RESULT], action[QUESTION_MODEL], action[FILE]);
      newState = Object.assign({}, state, { [SURVEY_RESULT]: surveyResult });
      break;
    case ADD_ANSWER_ACTION:
      surveyResult = handleAddMultiChoiceAnswer(state[SURVEY_RESULT], action[QUESTION_MODEL], action[ANSWER]);
      triggerState = handleTriggers(state[TRIGGER_STATE], action[QUESTION_MODEL], surveyResult);
      newState = Object.assign({}, state, {
        [SURVEY_RESULT]: surveyResult,
        [TRIGGER_STATE]: triggerState
      });
      break;
    case REMOVE_ANSWER_ACTION:
      surveyResult = handleRemoveAnswer(
        state[SURVEY_RESULT],
        action[QUESTION_MODEL],
        action[ANSWER],
        action[SURVEY_TEMPLATE]
      );
      triggerState = handleTriggers(state[TRIGGER_STATE], action[QUESTION_MODEL], surveyResult, false, action[ANSWER]);
      newState = Object.assign({}, state, {
        [SURVEY_RESULT]: surveyResult,
        [TRIGGER_STATE]: triggerState
      });
      break;
    case VALIDATE_ANSWER_ACTION: {
      let surveyValidation = ObjectUtils.deepClone(state[QUESTION_VALIDATIONS]);
      surveyValidation[action[QUESTION_NAME]] = action[VALIDATION] ? [action[VALIDATION]] : []; //todo: maybe to
      // add more than
      // one
      // validation?
      newState = Object.assign({}, state, { [QUESTION_VALIDATIONS]: surveyValidation });
      break;
    }
    case ADD_TABLE_ROW_ACTION:
      surveyResult = handleAddTableRow(state[SURVEY_RESULT], action[QUESTION_MODEL], action[FIRST_ONLY]);
      newState = Object.assign({}, state, { [SURVEY_RESULT]: surveyResult });
      break;
    case UPDATE_TABLE_ROW_ACTION:
      surveyResult = handleUpdateTableRow(
        state[SURVEY_RESULT],
        action[QUESTION_MODEL],
        action[ID],
        action[PROPERTY_NAME],
        action[PROPERTY_VALUE]
      );
      newState = Object.assign({}, state, { [SURVEY_RESULT]: surveyResult });
      break;
    case DELETE_TABLE_ROW_ACTION:
      surveyResult = handleDeleteTableRow(state[SURVEY_RESULT], action[QUESTION_MODEL], action[ID]);
      newState = Object.assign({}, state, { [SURVEY_RESULT]: surveyResult });
      break;
    case PROPERTY_CHANGED_ACTION:
      newState = Object.assign({}, state, {
        [action[PROPERTY_NAME]]: action[PROPERTY_VALUE]
      });
      break;
    case REORDER_QUESTION_ROW_ACTION:
      surveyResult = handleReorderTableRow(
        state[SURVEY_RESULT],
        action[QUESTION_NAME],
        action[ROW_TO_MOVE_ID],
        action[ROW_TARGET_ID]
      );
      newState = Object.assign({}, state, { [SURVEY_RESULT]: surveyResult });
      break;
    case IMPORT_TABLE_ROWS_ACTION:
      surveyResult = handleImportTableRows(
        state[SURVEY_RESULT],
        action[QUESTION_MODEL],
        action[ANSWER],
        action[IMPORT_DATA],
        action[OVERWRITE]
      );
      newState = Object.assign({}, state, { [SURVEY_RESULT]: surveyResult });
      break;
    default:
      newState = Object.assign({}, state, {});
      break;
  }
  return newState;
};

function findResultQuestion(surveyResult, questionName) {
  let question = findByObjectProp(surveyResult.questions, "name", questionName);

  if (!(question.name && question.answers)) {
    question = {
      name: questionName,
      answers: [],
      rows: []
    };
    surveyResult.questions.push(question);
  }

  return question;
}

function handleSetAnswer(surveyResult, questionModel, answer, prevent, surveyTemplate) {
  let surveyResultCopy = Object.assign({}, surveyResult);
  let question = findResultQuestion(surveyResultCopy, questionModel.name);

  // if question type is radio, by setting an answer we have to care about trigger that are connected to other
  // options of this question
  if (questionModel.type === "radio") {
    let questionAnswer = question.answers[0] ? question.answers[0].value : "";
    //radio question can have just one answer- so the triggered results for any other should be removed
    cleanTriggeredResults(surveyResultCopy, questionModel, questionAnswer, surveyTemplate);
  }
  if (!prevent) {
    question.answers = filterExcludeByTemplate(coalesce(question.answers, []), {
      label: opt(answer).label,
      colLabel: opt(answer).colLabel
    });
    if (answer) {
      question.answers.push(answer);
    }
  }
  //if something was changed, we should update row because of validation.
  question.rows = markAsDirtyRow(question, answer);
  return surveyResultCopy;
}

function markAsDirtyRow(question, answer) {
  if (opt(answer).label) {
    question.rows = ObjectUtils.deepClone(question.rows);
    let row = findByObjectProp(question.rows, "id", opt(answer).label);
    if (!row.id) {
      //if don't have the row, it means that is static table, or it is a new row from the space
      row = {
        id: opt(answer).label,
        name: opt(answer).label,
        value: opt(answer).label,
        isDynamic: false,
        title: ""
      };
      question.rows.push(row);
    }
    row.uid = uuid.v4();
  }

  return question.rows;
}

export function prepareResult(result) {
  // Verwende schnelleren, nativen Clone (sofern keine Funktionen im Objekt sind)
  const prepared = structuredClone(result);

  const questions = prepared.questions;
  if (!questions) return prepared;

  for (const q of questions) {
    const rows = q.rows;
    if (!rows) continue;

    for (const row of rows) {
      // Schnellerer UID (nicht 128 Bit, aber ausreichend eindeutig)
      row.uid = `r${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    }
  }

  return prepared;
}

function handleSelectFiles(surveyResult, questionModel, files) {
  let surveyResultCopy = Object.assign({}, surveyResult);
  let question = findResultQuestion(surveyResultCopy, questionModel.name);
  question.files = filterExcludeByTemplate(coalesce(question.files, []), {
    label: opt(files)[$FILES.row],
    colLabel: opt(files)[$FILES.row]
  });
  if (files) {
    question.files.push(files);
  }
  return surveyResultCopy;
}

function handleSuccessfulUpload(surveyResult, questionModel, file) {
  let surveyResultCopy = Object.assign({}, surveyResult);
  if (file) {
    let question = findResultQuestion(surveyResultCopy, questionModel.name);
    question.files = filterExcludeByTemplate(coalesce(question.files, []), file);
  }
  return surveyResultCopy;
}

function handleJumpToPage(state, action, triggerState) {
  return Object.assign({}, state, {
    [SURVEY_CURRENT_PAGE]: action[SURVEY_CURRENT_PAGE],
    [TRIGGER_STATE]: triggerState
  });
}

// This function is only called when adding a new value to check-box (in multiple or table question)
function handleAddMultiChoiceAnswer(surveyResult, questionModel, answer) {
  let surveyResultCopy = Object.assign({}, surveyResult);
  let question = findResultQuestion(surveyResultCopy, questionModel.name);
  let column = findByObjectProp(questionModel.columns, "name", answer.colLabel);
  let exclusive = coalesce(opt(column).choiceExclusive, questionModel.choiceExclusive);

  //all the answers which belongs to same sub-question
  let filteredAnswers = filterExcludeByTemplate(question.answers, {
    label: answer.label,
    colLabel: answer.colLabel
  });

  question.rows = markAsDirtyRow(question, answer);

  // just for table questions
  if (questionModel.type === "dynamicTable" || questionModel.type === "staticTable") {
    question.answers.forEach((questionAnswer) => {
      // if the answer is exclusive option- all the other answers for that sub-question should be removed (with
      // same label and colLabel)
      if (
        coalesce(column.choiceExclusive, "") === answer.value &&
        questionAnswer.colLabel === answer.colLabel &&
        questionAnswer.label === answer.label
      ) {
        question.answers = filteredAnswers;
      }
      // if exclusive option is already in result for some sub-question, it should be removed if some other
      // option is chosen
      else if (
        questionAnswer.value === coalesce(column.choiceExclusive, "") &&
        questionAnswer.colLabel === answer.colLabel &&
        questionAnswer.label === answer.label
      ) {
        question.answers = filteredAnswers;
      }
    });
  } else {
    // for simple multiple-choice question
    //if this answer is exclusive fot this question(or column/row), we will delete everything else for this
    // question.
    if (coalesce(answer.value, "") === exclusive) {
      question.answers = filteredAnswers;
    }
    //if exclusive exists in result but other option is checked- exclusive is automatically removed form result
    else if (findObjectIndexInArray(question.answers, "value", questionModel.choiceExclusive) !== -1) {
      question.answers = [];
    }
  }

  //before pushing an answer to answers row, we are checking if answer already exists in answers- this should never
  // happen, but if it does ->we will skip adding.
  if (findByTemplate(question.answers, answer).length === 0) {
    question.answers.push(answer);
  }

  return surveyResultCopy;
}

function handleRemoveAnswer(surveyResult, questionModel, answer, surveyTemplate) {
  let surveyResultCopy = Object.assign({}, surveyResult);
  let question = findResultQuestion(surveyResultCopy, questionModel.name);
  question.answers = filterExcludeByTemplate(question.answers, answer, ["text"]);

  // we need to care about results which are in triggered questions
  cleanTriggeredResults(surveyResultCopy, questionModel, answer.value, surveyTemplate);
  question.rows = markAsDirtyRow(question, answer);

  return surveyResultCopy;
}

// Function which cleans all the results when the trigger option is unchecked - all the chained question results needs
// to be removed
function cleanTriggeredResults(surveyResult, questionModel, answer, surveyTemplate) {

    let triggers = ObjectUtils.findArrayByObjectProp(questionModel.triggers, "answerValue", answer);
    triggers.forEach((trigger) => {

        if (trigger.id || questionModel.type === "radio") {
            let triggerTarget = findTriggerTargetQuestion(surveyResult.questions, trigger);
            if (triggerTarget) {
                if (triggerTarget.answers && triggerTarget.answers.length > 0) {
                    triggerTarget.answers = [];
                }
                if (triggerTarget.rows && triggerTarget.rows.length > 0) {
                    triggerTarget.rows = [];
                }

                let nextTriggerTarget;
                for (let i = 0; i < surveyTemplate.pages.length; i++) {
                    nextTriggerTarget = findTriggerTargetQuestion(surveyTemplate.pages[i].questions, triggerTarget);
                    if (nextTriggerTarget.name) {
                        break;
                    }
                }
                if (nextTriggerTarget.triggers && nextTriggerTarget.triggers.length > 0) {
                    for (let j = 0; j < nextTriggerTarget.triggers.length; j++) {
                        cleanTriggeredResults(
                            surveyResult,
                            nextTriggerTarget,
                            nextTriggerTarget.triggers[j].answerValue,
                            surveyTemplate
                        );
                    }
                }
            }
        }
    });
}


function handleAddTableRow(surveyResult, questionModel, firstOnly) {
  let surveyResultCopy = Object.assign({}, surveyResult);
  let question = findResultQuestion(surveyResultCopy, questionModel.name);
  let maxId = 0;
  if (!firstOnly || coalesce(getDeepProperty(question, "rows", "length"), 0) === 0) {
    question.rows.forEach((row) => {
      let currId = parseInt(row.id.replace("dynamic", ""));
      maxId = Math.max(currId, maxId);
    });
    maxId++;
    question.rows.push({
      id: "dynamic" + maxId,
      name: "dynamic" + maxId,
      value: "dynamic" + maxId,
      isDynamic: true,
      title: "",
      orderId: maxId - 1,
      uid: uuid.v4()
    });
  }
  return surveyResultCopy;
}

function handleUpdateTableRow(surveyResult, questionModel, rowId, propName, propValue) {
  let surveyResultCopy = Object.assign({}, surveyResult);
  let question = findResultQuestion(surveyResultCopy, questionModel.name);
  let row = findByObjectProp(question.rows, "id", rowId);
  row[propName] = propValue;
  return surveyResultCopy;
}

function handleDeleteTableRow(surveyResult, questionModel, rowId) {
  let surveyResultCopy = Object.assign({}, surveyResult);
  let question = findResultQuestion(surveyResultCopy, questionModel.name);
  if (question.name) {
    if (rowId === undefined) {
      question.answers = [];
      question.rows = [];
    } else {
      let row = findByObjectProp(question.rows, "id", rowId);
      if (row.id) {
        question.answers = removeObjectByProp(question.answers, "label", row.value);
        question.rows = removeObjectByProp(question.rows, "id", row.id);
      }
    }
  }
  return surveyResultCopy;
}

function handleImportTableRows(surveyResult, questionModel, answer, importData, overwrite) {
  let surveyResultCopy = Object.assign({}, surveyResult);
    //let question = findResultQuestion(surveyResult, questionModel.name);
  //prepare column-choices-map for performance optimisation
  const columnChoiceMaps = new Map();
  let tableColumns = questionModel.columns;
    tableColumns.forEach((col) => {
        if (col.choices) {
            const map = new Map(col.choices.map((c) => [c.text.trim(), c]));
            columnChoiceMaps.set(col.text, map);
        }
    });

  let maxId = overwrite || !answer.answers?.length
    ? 0
    : Math.max(...answer.rows.map(r => parseInt(r.id.replace("dynamic", ""))) );

  if (maxId === 0) {
    answer.rows = [];
    answer.answers = [];
  }

  for (const importRow of importData) {
    const rowAnswers = [];
    const rows = [];
    const rowId = `dynamic${++maxId}`;
    const row = { id: rowId, name: rowId, value: rowId, isDynamic: true, orderId: maxId };
    rows.push(row);

    for (const tableColumn of tableColumns) {

      let answerValue = importRow[tableColumn.text] ?? importRow["\uFEFF" + tableColumn.text];
      if (!answerValue && tableColumn.type !== "Checkbox") continue;

      if (tableColumn.type === "Date") {
        answerValue = parseImportedSurveyDate(answerValue, questionModel.dateFormat).toISOString();
      } else if (tableColumn.type === "Radio" && tableColumn.hasOther && answerValue === "Sonstiges") {
        let otherAnswerValue = importRow[tableColumn.text + OTHER_ANSWER_SUFFIX];
          if (otherAnswerValue) {
            rowAnswers.push(makeAnswer(otherAnswerValue, row, tableColumn, "OtherAnswer"));
          }
        continue;
      }

      let optionValue = answerValue;

      if (["Radio", "Dropdown", "predefinedList", "Checkbox"].includes(tableColumn.type)) {
        const exclusive = coalesce(
          getDeepProperty(questionModel, "tempChoiceExclusive"),
          getDeepProperty(tableColumn, "choiceExclusive")
        );
        let foundExclusive = false;
        let tempAnswers = [];
        //get column-choice-map
        //const choiceMap = columnChoiceMaps.get(tableColumn.text);
        //const choice = choiceMap?.get(answerValue.trim());
        tableColumn.choices.forEach((choice) => {
            optionValue = choice.value;
            if (tableColumn.type === "Checkbox" && !foundExclusive) {
                answerValue = importRow[tableColumn.text + OPTION_DELIMITER + choice.text];
                if (exclusive && choice.name === exclusive && (choice.text.trim() === answerValue.trim() ||  (tableColumn.type === "Checkbox" && "Ja" === answerValue.trim()))) {
                    foundExclusive = true;
                    rowAnswers.push(makeAnswer(optionValue, row, tableColumn, choice.text));
                }
            }
            if (answerValue && (choice.text.trim() === answerValue.trim() || (tableColumn.type === "Checkbox" && "Ja" === answerValue.trim())) && !foundExclusive) {
                tempAnswers.push(makeAnswer(optionValue, row, tableColumn, choice.text));
            }
        });
        if (!foundExclusive && tempAnswers) {
          tempAnswers.forEach((tempAnswer) => {
            rowAnswers.push(tempAnswer);
          });
        }
        if (
          ((tableColumn.type === "Checkbox" && (!exclusive || !foundExclusive)) || answerValue === "Sonstiges") &&
          tableColumn.hasOther
        ) {
          let otherAnswerValue = importRow[tableColumn.text + OTHER_ANSWER_SUFFIX];
          if (otherAnswerValue) {
            rowAnswers.push(makeAnswer(otherAnswerValue, row, tableColumn, "OtherAnswer"));
          }
        }
      } else {

        rowAnswers.push(makeAnswer(optionValue, row, tableColumn, answerValue));
      }
    };
    answer.answers.push(...rowAnswers);
    answer.rows.push(...rows);
  };
  return surveyResultCopy;
}

function handleReorderTableRow(surveyResult, questionName, rowToMoveId, targetRowId) {
  let surveyResultCopy = Object.assign({}, surveyResult);

  let indexOfQuestion = ObjectUtils.findObjectIndexInArray(surveyResult.questions, "name", questionName);
  let answer = ObjectUtils.findByObjectProp(surveyResult.questions, "name", questionName);
  let oldIndex = ObjectUtils.findObjectIndexInArray(answer.rows, [ID], rowToMoveId);
  let indexToTake = ObjectUtils.findObjectIndexInArray(answer.rows, [ID], targetRowId);

  let i;
  if (indexToTake > oldIndex) {
    let temp = answer.rows[oldIndex];

    i = oldIndex + 1;
    for (i; i <= indexToTake; i++) {
      let j = i - 1;
      answer.rows[j] = answer.rows[i];
    }
    answer.rows[indexToTake] = temp;
  }
  if (indexToTake < oldIndex) {
    let temp = answer.rows[oldIndex];
    i = oldIndex - 1;
    for (i; i >= indexToTake; i--) {
      let k = i + 1;
      answer.rows[k] = answer.rows[i];
    }
    answer.rows[indexToTake] = temp;
  }

  //after every reordering we need to update orderId of every row
  for (let a = 0; a < answer.rows.length; a++) {
    answer.rows[a].orderId = a;
  }
  surveyResultCopy.questions[indexOfQuestion] = answer;
  return surveyResultCopy;
}

function getTriggerTargetKey(trigger) {
  return coalesce(trigger.targetKey, trigger.targetName);
}

function findTriggerTargetQuestion(questions, trigger) {
  let triggerTarget = {};
  if (trigger.targetKey) {
    triggerTarget = ObjectUtils.findByObjectProp(questions, "key", trigger.targetKey);
  }
  if (!triggerTarget.name && trigger.targetName) {
    triggerTarget = ObjectUtils.findByObjectProp(questions, "name", trigger.targetName);
  }
  return triggerTarget;
}

function handleTriggers(triggerState, questionModel, surveyResult, isThisAddingOfAnAnswer = true, answer) {
  let result = triggerState;
  let triggers = getDeepProperty(questionModel, "triggers");
  if (triggers) {
    // if handleTriggers is called by removeAnswer action- property isThisAddingOfAnAnswer is false
    // which means we have to check all the chained triggers and remove them from triggerState. Same situation is if
    // question type is radio - then handleTriggers is called by setAnswer action
    if ((!isThisAddingOfAnAnswer || questionModel.type === "radio") && answer) {
      let selectedOptionTrigger = ObjectUtils.findByObjectProp(triggers, "answerValue", answer.value);
      if (selectedOptionTrigger.id || questionModel.type === "radio") {
        // trigger which belongs to current question
        //let triggerObject = ObjectUtils.findByObjectProp(result, "questionName", questionModel.name);
        // we are sending list of triggers, target question for this trigger and answer (if this answer doesn't
        // have trigger- nothing will happen)
        result = cleanNextChainedTrigger(result, getTriggerTargetKey(selectedOptionTrigger));
      }
    }
    //remove all triggers created by this question;
    result = removeObjectByProp(result, "questionName", questionModel.name);
    //try to find question result, and set new triggers for the existing answers;
    let question = findResultQuestion(surveyResult, questionModel.name);
    let answerIndex = indexByProperty(question.answers, "value");
    triggers.forEach((currTrigger) => {
      let idx = coalesce(getDeepProperty(answerIndex, currTrigger.answerValue, "indexes", 0), -1);
      if (idx > -1) {
        let triggerAction = {
          questionName: questionModel.name,
          questionKey: questionModel.key,
          targetName: currTrigger.targetName,
          targetKey: currTrigger.targetKey,
          targetType: currTrigger.targetType
        };
        result.push(triggerAction);
      }
    });
  }
  return result;
}

function cleanNextChainedTrigger(result, questionModelIdentifier) {
  if (!questionModelIdentifier) {
    return result;
  }
  // looking is triggered question also trigger for some other question- because wee need to clean them all
  let nextTrigger = ObjectUtils.findByObjectProp(result, "questionKey", questionModelIdentifier);
  if (!nextTrigger.questionName) {
    nextTrigger = ObjectUtils.findByObjectProp(result, "questionName", questionModelIdentifier);
  }
  if (nextTrigger.questionName) {
    result = nextTrigger.questionKey
      ? removeObjectByProp(result, "questionKey", nextTrigger.questionKey)
      : removeObjectByProp(result, "questionName", nextTrigger.questionName);
    result = cleanNextChainedTrigger(result, getTriggerTargetKey(nextTrigger));
  }
  return result;
}

function refershTriggers(surveyTemplate, surveyResult) {
  let result = [];
  coalesce(getDeepProperty(surveyTemplate, "pages"), []).forEach((page) => {
    coalesce(getDeepProperty(page, QUESTIONS), []).forEach((question) => {
      result = handleTriggers(result, question, surveyResult);
    });
  });
  return result;
}

export function jumpToPage(page) {
  return (dispatch, getState) => {
    dispatch(propertyChange("surveyCurrentPage", ""));
    if (
      !(
        getState().surveyView.surveyResult.readOnly ||
        getState().surveyView.editingDisabled ||
        getState().survey.showSurveyPreview
      ) &&
      getState().surveyView.surveyResultChanged
    ) {
      dispatch(saveSurveyResult(getState().surveyView.surveyResult, true));
    }
    let surveyTemplate = getState().survey[SURVEY_TEMPLATE];

    dispatch({
      [TYPE_KEY]: JUMP_TO_PAGE_ACTION,
      [SURVEY_CURRENT_PAGE]: page,
      [SURVEY_TEMPLATE]: surveyTemplate
    });
    dispatch(propertyChange(SURVEY_RESULT_CHANGED, false));
  };
}

export function showHideOverview() {
  return (dispatch, getState) => {
    let show = !getState().surveyView[SHOW_OVERVIEW_MENU];
    dispatch({
      [TYPE_KEY]: SHOW_HIDE_OVERVIEW_ACTION,
      [SHOW_OVERVIEW_MENU]: show
    });
  };
}

export function resetSurveyView() {
  return (dispatch) => {
    dispatch({
      [TYPE_KEY]: RESET_ACTION
    });
    dispatch(resetSurveyRunner());
  };
}

export function reorderQuestionRow(questionName, rowId, targetRow) {
  return {
    [TYPE_KEY]: REORDER_QUESTION_ROW_ACTION,
    [QUESTION_NAME]: questionName,
    [ROW_TO_MOVE_ID]: rowId,
    [ROW_TARGET_ID]: targetRow
  };
}

export function validateQuestionAnswer(questionModel, answer) {
  let validate = validateAnswer(questionModel, answer);
  return (dispatch) => {
    validate && dispatch(validate);
  };
}

export function setQuestionAnswer(questionModel, answer, validateOnly = false) {
  let validate = validateAnswer(questionModel, answer);

  return (dispatch, getState) => {
    let surveyTemplate = getState().survey.surveyTemplate;
    let preventUpdate = false;
    if (validate && !validateOnly) {
      dispatch(validate);
      preventUpdate = validate[VALIDATION] && !validate[VALIDATION].isOk && validate[VALIDATION][PREVENT_UPDATE];
    }

    dispatch({
      [TYPE_KEY]: SET_ANSWER_ACTION,
      [ANSWER]: answer,
      [QUESTION_MODEL]: questionModel,
      [PREVENT_UPDATE]: preventUpdate,
      [SURVEY_TEMPLATE]: surveyTemplate
    });
    dispatch(propertyChange(SURVEY_RESULT_CHANGED, true));
  };
}

export function downloadFile(file) {
  return () => {
    let index = file.lastIndexOf(".");
    let fileName = index >= 0 ? file.substring(0, index) : file;
    let extension = index >= 0 ? file.substring(index + 1) : "";
    let url = `/survey/surveys/download/${fileName}/${extension}`;
    rest.doGet(url).then((json) => {
      //download(`data:${json.contentType};base64${json.fileSource}`, json.fileName, json.contentType);
      download(`data:${json.contentType};base64,${json.fileSource}`, json.fileName, json.contentType);
    });
  };
}

export function uploadFiles(question, files = []) {
  return (dispatch) => {
    let url = `/survey/surveys/upload`;
    for (let i = 0; i < files.length; i++) {
      let file = files[i].name;
      let reader = new FileReader();
      let selectedFile = files[i];
      reader.onload = function () {
        let data = {
          fileName: file,
          fileSource: this.result.substring(this.result.lastIndexOf(",") + 1)
        };
        rest.doPostFileUpload(url, data).then((json) => {
          if (json.fileNameOnServer) {
            addQuestionAnswer(question, {
              name: json.filePathOnServer,
              value: json.fileNameOnServer
            })(dispatch);
            dispatch(successfulUpload(question, selectedFile));
            dispatch(messagesReceived(createSuccessMessages("Upload successful " + json.fileNameOnServer)));
          } else {
            dispatch(messagesReceived(createErrorMessages(json)));
          }
        });
      };
      reader.readAsDataURL(files[i]);
    }
  };
}

export function successfulUpload(questionModel, file) {
  return {
    [TYPE_KEY]: SUCCESSFUL_UPLOAD_ACTION,
    [FILE]: file,
    [QUESTION_MODEL]: questionModel
  };
}

export function selectFiles(questionModel, files) {
  let validate = validateFiles(questionModel, files);
  return (dispatch) => {
    let preventUpdate = false;

    if (validate) {
      dispatch(validate);
      preventUpdate =
        !getDeepProperty(validate, VALIDATION, "isOk") && getDeepProperty(validate, VALIDATION, PREVENT_UPDATE);
    }

    dispatch({
      [TYPE_KEY]: SELECT_FILES_ACTION,
      [FILES]: files,
      [QUESTION_MODEL]: questionModel,
      [PREVENT_UPDATE]: preventUpdate
    });
  };
}

export function addQuestionAnswer(questionModel, answer) {
  return (dispatch) => {
    dispatch({
      [TYPE_KEY]: ADD_ANSWER_ACTION,
      [ANSWER]: answer,
      [QUESTION_MODEL]: questionModel
    });
    dispatch(propertyChange(SURVEY_RESULT_CHANGED, true));
  };
}

export function removeQuestionAnswer(questionModel, answer) {
  return (dispatch, getState) => {
    let surveyTemplate = getState().survey.surveyTemplate;
    dispatch({
      [TYPE_KEY]: REMOVE_ANSWER_ACTION,
      [ANSWER]: answer,
      [QUESTION_MODEL]: questionModel,
      [SURVEY_TEMPLATE]: surveyTemplate
    });
    dispatch(propertyChange(SURVEY_RESULT_CHANGED, true));
  };
}

export function importRows(questionModel, answer, importData, overwrite = false) {
  return async (dispatch, getState) => {
    dispatch(beginLoading());

    try {
      await new Promise((resolve) => setTimeout(resolve, 0));
      const chunks = getImportDataChunks(importData);

      for (let i = 0; i < chunks.length; i++) {
        await dispatch({
          [TYPE_KEY]: IMPORT_TABLE_ROWS_ACTION,
          [QUESTION_MODEL]: questionModel,
          [ANSWER]: answer,
          [IMPORT_DATA]: chunks[i],
          [OVERWRITE]: overwrite && i === 0
        });

        if (i < chunks.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
      }

      if (!getState().survey.showSurveyPreview) {
        await dispatch(saveSurveyResult(getState().surveyView.surveyResult, true, false));
      }
    } finally {
      dispatch(endLoading());
    }
  };
}

function getImportDataChunks(importData) {
  if (!CHUNKED_TABLE_IMPORT_ENABLED || !importData || importData.length <= TABLE_IMPORT_CHUNK_SIZE) {
    return [importData];
  }

  let chunks = [];
  for (let i = 0; i < importData.length; i += TABLE_IMPORT_CHUNK_SIZE) {
    chunks.push(importData.slice(i, i + TABLE_IMPORT_CHUNK_SIZE));
  }
  return chunks;
}

export function addRow(questionModel, firstOnly = false) {
  return {
    [TYPE_KEY]: ADD_TABLE_ROW_ACTION,
    [QUESTION_MODEL]: questionModel,
    [FIRST_ONLY]: firstOnly
  };
}

export function downloadCSVTemplate(question) {
  return (dispatch, getState) => {
    const store = getState()[SURVEY];
    const reportPeriodGid = getState().surveyView[SURVEY_RESULT]?.reportPeriodGid;

    dispatch(beginLoading());
    const url = reportPeriodGid ? `/admin/admin/surveys/${store.identifier}/version/${store.surveyTemplate.version}/tableResult/${reportPeriodGid}/${question.name}`:
    `/admin/admin/surveys/${store.identifier}/version/${store.surveyTemplate.version}/csvtemplate/${question.name}`;
    rest
      .doExport(
        url,
        "application/excel"
      )
      .then((response) => response.blob())
      .then((blob) => {
        let FileSaver = require("file-saver");
        let title = cleanText(question.title)
          .replace(/\s+/g, "_")
          .replace(/[\\/:*"<>|]/g, "_");
        title = ObjectUtils.trunc(title, 70);

        let fileNameForDownload = `template-${title}.xlsx`;

        dispatch(endLoading());

        FileSaver.saveAs(blob, fileNameForDownload);
      });
  };
}

export function loadPreviousPeriodQuestionAnswers(question) {
  return (dispatch, getState) => {
    const reportPeriodGid = getState().surveyView[SURVEY_RESULT].reportPeriodGid;

    if (!reportPeriodGid) {
      dispatch(
        showDialog(
          {},
          () => {},
          "user.surveyView.dialog.load.prev.result.not.found.title",
          "user.surveyView.dialog.load.prev.result.not.found.text",
          "confirmDialog.ok",
          null
        )
      );
      return;
    }

    rest.doGet(`/survey/surveys/result/${reportPeriodGid}/previous`).then(
      (json) => {
        if (json[roModel.reportPeriodGid]) {
          const questionAnswersPrev = json.questions.find((q) => q.questionKey === question.key);

          if (questionAnswersPrev) {
            dispatch(
              showDialog(
                { questionAnswersPrev },
                (data) => {
                  const surveyResult = ObjectUtils.deepClone(getState().surveyView.surveyResult);

                  let curQuestion = surveyResult.questions.find((q) => q.name === question.name);
                  processOptionAnswers(
                    curQuestion,
                    question,
                    data.questionAnswersPrev.answers,
                    data.questionAnswersPrev.rows
                  );

                  dispatch({
                    [TYPE_KEY]: LOAD_SURVEY_RESULT_ACTION,
                    [RESULT_VALUE]: surveyResult
                  });
                  refreshTriggerAction()(dispatch, getState);
                  dispatch(saveSurveyResult(getState().surveyView.surveyResult, true));
                },
                "user.surveyView.dialog.load.prev.result.title",
                "user.surveyView.dialog.load.prev.result.text",
                "confirmDialog.ok",
                "confirmDialog.cancel"
              )
            );
          } else {
            dispatch(
              showDialog(
                {},
                () => {},
                "user.surveyView.dialog.load.prev.result.not.found.title",
                "user.surveyView.dialog.load.prev.result.not.found.text",
                "confirmDialog.ok",
                null
              )
            );
          }
        } else {
          dispatch(messagesReceived(createErrorMessages(json)));
        }
      },
      (reason) => {
        if (reason.status === 404) {
          dispatch(
            showDialog(
              {},
              () => {},
              "user.surveyView.dialog.load.prev.result.not.found.title",
              "user.surveyView.dialog.load.prev.result.not.found.text",
              "confirmDialog.ok",
              null
            )
          );
        } else {
          dispatch(messagesReceived(createBackendErrorMessages(reason)));
        }
      }
    );
  };
}

function processOptionAnswers(curQuestion, question, answers, rows) {
  let returnAnswers = [];
  let returnRows = [];
  if (answers && answers.length && answers.length > 0) {
    answers.forEach((answer) => {
            // Only process answer if columnKey matches a column in question.columns AND colType matches
        const matchingColumn = question.columns && answer.columnKey
            ? findByObjectProp(question.columns, "key", answer.columnKey)
            : null;

        if (!isSimpleType(question.type) && (!matchingColumn || matchingColumn.type !== answer.columnType)) {
            return; // skip this answer
        }

        let row = findByObjectProp(rows, "name", answer.label);
        if (row) {
          let originalRow = findByObjectProp(question.rows, "name", answer.label);
          if (originalRow) {
            row.text = originalRow.text;
            row.key = originalRow.key;
          }
          if (returnRows.indexOf(row) === -1) {
            returnRows.push(row);
          }
        }
      if (
        answer.columnType === "Radio" ||
        answer.columnType === "Dropdown" ||
        answer.columnType === "predefinedList" ||
        answer.columnType === "Checkbox"
      ) {

        matchingColumn.choices &&
          matchingColumn.loadPreviousDataAllowed &&
          matchingColumn.choices.forEach((choice) => {
            if (answer.value && choice.value === answer.value) {
              returnAnswers.push(makeAnswer(choice.value, row, matchingColumn, choice.name));
            }
          });

        if (answer.name === "OtherAnswer" && matchingColumn.loadPreviousDataAllowed && matchingColumn.hasOther) {
          if (answer.value) {
            returnAnswers.push(makeAnswer(answer.value, row, matchingColumn, "OtherAnswer"));
          }
        }
      } else if (
        question.type === "radio" ||
        question.type === "dropdown" ||
        question.type === "predefinedList" ||
        question.type === "checkbox"
      ) {
        if (answer.name === "OtherAnswer" && question.hasOther) {
          if (answer.value) {
            returnAnswers.push(makeSimpleAnswer(answer.value, question, "OtherAnswer"));
          }
        } else {
          let choice = findByObjectProp(question.choices, "name", answer.name);
          if (choice) {
            returnAnswers.push(makeSimpleAnswer(choice.value, question, choice.name));
          }
        }
      } else {
        returnAnswers.push(answer);
      }
    });
  }
  curQuestion.answers = returnAnswers;
  curQuestion.rows = returnRows.sort((a,b) => a.orderId - b.orderId);
  return curQuestion;
}

export function deleteRows(questionModel, rowIds) {
  return (dispatch) => {
    for(const rowId of rowIds) {
      dispatch({
        [TYPE_KEY]: DELETE_TABLE_ROW_ACTION,
        [QUESTION_MODEL]: questionModel,
        [ID]: rowId
      });
    }

    dispatch(propertyChange(SURVEY_RESULT_CHANGED, true));
  };
}

export function deleteRow(questionModel, rowId) {
  return (dispatch) => {
    dispatch(
      showDialog(
        {},
        () => {
          dispatch({
            [TYPE_KEY]: DELETE_TABLE_ROW_ACTION,
            [QUESTION_MODEL]: questionModel,
            [ID]: rowId
          });
          dispatch(propertyChange(SURVEY_RESULT_CHANGED, true));
        },
        "",
        rowId ? "user.surveyView.dialog.confirm.delete.single.row" : "user.surveyView.dialog.confirm.delete.all.rows",
        "confirmDialog.confirmLabel",
        "confirmDialog.dismissLabel"
      )
    );
  };
}

export function refreshTriggerAction() {
  return (dispatch, getState) => {
    let surveyTemplate = getState().survey[SURVEY_TEMPLATE];
    dispatch({
      [TYPE_KEY]: REFRESH_TRIGGERS_ACTION,
      [SURVEY_TEMPLATE]: surveyTemplate
    });
  };
}

function validateAnswer(questionModel, answer) {
  let result;
  if (questionModel) {
    let validators = findByTemplate(coalesce(getDeepProperty(questionModel, "validators"), []), {
      [COLUMN_NAME]: answer.colLabel
    });
    if (validators[0]) {
      let validation = ValidatorUtils.validate(answer.value, validators[0]);

      if (validation) {
        validation[COLUMN_NAME] = answer.colLabel;
        validation[ROW_NAME] = answer.label;
      }

      result = {
        [TYPE_KEY]: VALIDATE_ANSWER_ACTION,
        [VALIDATION]: validation,
        [QUESTION_NAME]: questionModel.name
      };
    }
  }
  return result;
}

function validateFiles(questionModel, files) {
  let result;
  if (questionModel) {
    let validators = findByTemplate(coalesce(getDeepProperty(questionModel, "validators"), []), {
      [COLUMN_NAME]: files[$FILES.columnName]
    });
    if (validators[0]) {
      let validation = ValidatorUtils.validate(getDeepProperty(files, $FILES.files), validators[0]);

      if (validation) {
        validation[COLUMN_NAME] = files[$FILES.columnName];
        validation[ROW_NAME] = files[$FILES.columnName];
      }

      result = {
        [TYPE_KEY]: VALIDATE_ANSWER_ACTION,
        [VALIDATION]: validation,
        [QUESTION_NAME]: questionModel.name
      };
    }
  }
  return result;
}

export function propertyChange(propertyName, propertyValue) {
  return {
    [TYPE_KEY]: PROPERTY_CHANGED_ACTION,
    [PROPERTY_NAME]: propertyName,
    [PROPERTY_VALUE]: propertyValue
  };
}

export default reducer;
